import { Component, computed, effect, inject, Signal } from '@angular/core';
import {
    ComponentsModule,
    DialogService,
    SnackBarService,
    TabItem
} from '@mastechnicaegroup/carelinelive-ui';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { CommissionerService } from '@lib/commissioner/commissioner.service';
import { Commissioner } from '@lib/api/models/commissioner';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';
import { AsyncPipe } from '@angular/common';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { PromptCommissionerStatusChangeDialogComponent } from '../../dialogs/commissioner/prompt/prompt-commissioner-status-change-dialog.component';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { postParamNamesFilterActive, postParamNamesFilterQuery } from '@shared/filters/filter-params';

@Component({
    selector: 'app-commissioners-index-page',
    imports: [
        ComponentsModule, AsyncPipe, PaginationCardComponent, RouterLink, ReactiveFormsModule, HeaderComponent, ErrorPanelComponent
    ],
    templateUrl: './commissioners-index-page.component.html',
    standalone: true,
    styleUrl: './commissioners-index-page.component.scss'
})
export class CommissionersIndexPageComponent {

    private readonly _activeRoute = inject(ActivatedRoute);
    private readonly _authService = inject(AuthenticationService);
    private readonly _commissionerService = inject(CommissionerService);
    private readonly _router = inject(Router);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _dialog = inject(DialogService);
    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '0',
            title: 'All'
        },
        {
            disabled: false,
            id: '1',
            title: 'Active'
        },
        {
            disabled: false,
            id: '2',
            title: 'Inactive'
        }
    ];

    protected user = toSignal(this._authService.user$);

    protected currentTabId$ = this._activeRoute.queryParams.pipe(
        map((params): string => params['tab'] ?? '0'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    protected readonly page = toSignal<number>(this._activeRoute.queryParams.pipe(
        map((params): number => params['page'] ?? 1)
    ));

    /** The search form control */
    protected readonly searchControl = new FormControl<string>(
        this._activeRoute.snapshot.queryParams['q'] ?? '',
        [Validators.maxLength(50)]
    );

    /** An observable that emits the search query */
    protected readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),

        // Only fire once every 100ms
        debounceTime(100),

        // If it's an empty string, return null
        map(value => !value ? undefined : value)
    ));

    /** Fetch the page of commissioners */
    protected readonly pageResult: Signal<Result<PaginatedApiResponse<Commissioner>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        searchQuery: toObservable(this.searchQuery),
        tabId: this.currentTabId$,
        refresh: this.refreshTrigger$.asObservable()
    }).pipe(
        switchMap(({ page, searchQuery, tabId }) => this._commissionerService.list(page, {
            [postParamNamesFilterQuery]: searchQuery,
            [postParamNamesFilterActive]: tabId === '0' ? '' : tabId === '1' ? 'true' : 'false'
        }).pipe(
            // correct out of page errors
            tap(response => {
                if (response.meta.current_page > response.meta.last_page) {
                    this.setPage(response.meta.last_page);
                }
            }),
            // Turn into a Result
            toResult()
        )),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta data from the page result
        return this.pageResult()?.value?.meta;
    });

    constructor() {
        // Update the query params when the page or search query changes
        effect(() => {
            this._router.navigate([], {
                relativeTo: this._activeRoute,
                queryParams: {
                    q: this.searchQuery()
                },
                queryParamsHandling: 'merge',

                // Don't push a new history state, just replace the query param
                replaceUrl: true
            });
        });
    }

    async currentTabIdChange($event: string) {
        await this._router.navigate([], {
            queryParams: { tab: $event },
            queryParamsHandling: 'merge'
        });
    }

    navigateToAddCommissioner() {
        this._router.navigate(['/add-commissioner']);
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }

    changeStatus(commissioner: Commissioner, status: boolean) {

        const commissionerData = {
            ...commissioner,
            status: status
        };

        return PromptCommissionerStatusChangeDialogComponent.show(this._dialog, commissionerData).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Commissioner status failed to change',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }

}
